import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearAuthSession, getAuthUser } from '../services/auth';
import BrandLockup from './BrandLockup';
import { useActor } from '../app/ActorContext';
import './Navbar.css';

// PrimeIcons 6.0.1 has no laboratory glyph, so the API Lab uses an inline flask
// that inherits currentColor and the surrounding font size like a `pi` icon does.
const NAV_SVG_ICONS = {
  flask: (
    <svg className="nb-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M9.5 3h5" />
      <path d="M10.5 3v6.2L5.3 18a2 2 0 0 0 1.7 3h10a2 2 0 0 0 1.7-3l-5.2-8.8V3" />
      <path d="M7.8 14.5h8.4" />
    </svg>
  ),
};

export const NavItemIcon = ({ icon }) => (NAV_SVG_ICONS[icon] ? NAV_SVG_ICONS[icon] : <i className={`pi ${icon}`}></i>);

const NAV_ITEMS = [
  { label: 'Overview', icon: 'pi-home', path: '/dashboard', match: (p) => p === '/dashboard' },
  { label: 'Properties', icon: 'pi-building', path: '/properties', match: (p) => p.startsWith('/properties') },
  { label: 'Documents', icon: 'pi-folder', path: '/documents', match: (p) => p.startsWith('/documents') },
];

export const ANALYSIS_ITEMS = [
  {
    label: 'BRRRR Goal Posts & North Stars',
    description: 'Core deal criteria and operating skills.',
    icon: 'pi-check-circle',
    path: '/research/brrrr-buy-box',
    match: (p) => p === '/research/brrrr-buy-box' || p.startsWith('/research/brrrr-buy-box/'),
  },
  {
    label: 'U.S. Market Research',
    description: 'State, metro, and national cash-flow rankings.',
    icon: 'pi-search',
    path: '/research',
    match: (p) => p === '/research' || p === '/research/',
  },
  {
    label: 'Region Research',
    description: 'Open a specific market page for deeper analysis.',
    icon: 'pi-map-marker',
    path: '/research/markets',
    match: (p) => p === '/research/markets' || p.startsWith('/research/markets/'),
  },
  {
    label: 'API Lab',
    description: 'Run controlled listing tests and inspect complete responses.',
    icon: 'flask',
    path: '/research/api-lab',
    match: (p) => p === '/research/api-lab' || p === '/research/rentcast-api-lab',
  },
  {
    label: 'Lead Underwriting',
    description: 'Review promoted leads through detailed BRRRR underwriting.',
    icon: 'pi-chart-bar',
    path: '/lead-underwriting',
    match: (p) => p.startsWith('/lead-underwriting'),
  },
  {
    label: 'Data Warehouse Audit',
    description: 'Pipeline health, freshness, and source trust.',
    icon: 'pi-database',
    path: '/research/data-warehouse-audit',
    match: (p) => p === '/research/data-warehouse-audit' || p.startsWith('/research/data-warehouse-audit/'),
  },
];

const getBrandTitle = (pathname) => {
  if (pathname === '/dashboard') return 'Dashboard';
  if (pathname.startsWith('/properties')) return 'Properties';
  if (pathname.startsWith('/documents')) return 'Documents';
  if (pathname.startsWith('/team')) return 'Team Access';
  if (pathname.startsWith('/contractors')) return 'Contractors';
  if (pathname.startsWith('/research/brrrr-buy-box')) return 'BRRRR Goal Posts & North Stars';
  if (pathname.startsWith('/research/api-lab') || pathname.startsWith('/research/rentcast-api-lab')) return 'API Lab';
  if (pathname.startsWith('/research/markets')) return 'Region Research';
  if (pathname.startsWith('/research/data-warehouse-audit')) return 'Data Warehouse Audit';
  if (pathname.startsWith('/research')) return 'U.S. Market Research';
  if (pathname.startsWith('/lead-underwriting')) return 'Lead Underwriting';
  return 'Dashboard';
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const username = getAuthUser();
  const { hasPermission } = useActor();
  const [menuOpen, setMenuOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [drawerAnalysisOpen, setDrawerAnalysisOpen] = useState(false);
  const analysisRef = useRef(null);

  const analysisActive = ANALYSIS_ITEMS.some(item => item.match(location.pathname));
  const brandTitle = getBrandTitle(location.pathname);

  useEffect(() => {
    setAnalysisOpen(false);
    setDrawerAnalysisOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!analysisOpen) return undefined;

    const handlePointerDown = (event) => {
      if (analysisRef.current && !analysisRef.current.contains(event.target)) {
        setAnalysisOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setAnalysisOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [analysisOpen]);

  const handleLogout = () => {
    clearAuthSession();
    navigate('/login');
  };

  const go = (path) => {
    navigate(path);
    setMenuOpen(false);
    setAnalysisOpen(false);
    setDrawerAnalysisOpen(false);
  };

  const toggleMobileMenu = () => {
    setMenuOpen(open => {
      const nextOpen = !open;
      if (!nextOpen) {
        setDrawerAnalysisOpen(false);
      }
      return nextOpen;
    });
  };

  return (
    <nav className="nb">
      <div className="nb-inner">
        <button type="button" className="nb-brand" onClick={() => go('/dashboard')} aria-label="Go to dashboard">
          <BrandLockup className="brand-lockup--nav" title={brandTitle} />
        </button>

        {/* Desktop nav links */}
        <div className="nb-links">
          {NAV_ITEMS.slice(0, 2).map(item => (
            <button
              key={item.path}
              className={`nb-link${item.match(location.pathname) ? ' nb-link--active' : ''}`}
              onClick={() => go(item.path)}
            >
              <NavItemIcon icon={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
          <div className="nb-mega" ref={analysisRef}>
            <button
              className={`nb-link nb-link--mega${analysisActive ? ' nb-link--active' : ''}${analysisOpen ? ' nb-link--open' : ''}`}
              onClick={() => setAnalysisOpen(open => !open)}
              aria-haspopup="menu"
              aria-expanded={analysisOpen}
            >
              <i className="pi pi-chart-bar"></i>
              <span>Research & Analysis</span>
              <i className="pi pi-chevron-down nb-link-chevron"></i>
            </button>

            {analysisOpen && (
              <div className="nb-mega-panel" role="menu">
                {ANALYSIS_ITEMS.map(item => (
                  <button
                    key={item.path}
                    className={`nb-mega-item${item.match(location.pathname) ? ' nb-mega-item--active' : ''}`}
                    onClick={() => go(item.path)}
                    role="menuitem"
                  >
                    <span className="nb-mega-icon"><NavItemIcon icon={item.icon} /></span>
                    <span>
                      <span className="nb-mega-title">{item.label}</span>
                      <span className="nb-mega-subtitle">{item.description}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {NAV_ITEMS.slice(2).map(item => (
            <button
              key={item.path}
              className={`nb-link${item.match(location.pathname) ? ' nb-link--active' : ''}`}
              onClick={() => go(item.path)}
            >
              <NavItemIcon icon={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
          {hasPermission('membership.manage') && (
            <button className={`nb-link${location.pathname.startsWith('/team') ? ' nb-link--active' : ''}`} onClick={() => go('/team')}>
              <i className="pi pi-users"></i><span>Team</span>
            </button>
          )}
        </div>

        {/* Right side */}
        <div className="nb-right">
          {username && <span className="nb-user">{username}</span>}
          <button className="nb-logout" onClick={handleLogout} title="Sign out">
            <i className="pi pi-sign-out"></i>
          </button>
          {/* Hamburger */}
          <button className="nb-hamburger" onClick={toggleMobileMenu} aria-label="Menu">
            <span className={`nb-ham-line${menuOpen ? ' nb-ham-line--open' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="nb-drawer">
          {NAV_ITEMS.slice(0, 2).map(item => (
            <button
              key={item.path}
              className={`nb-drawer-link${item.match(location.pathname) ? ' nb-drawer-link--active' : ''}`}
              onClick={() => go(item.path)}
            >
              <NavItemIcon icon={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
          <button
            className={`nb-drawer-link nb-drawer-link--parent${analysisActive ? ' nb-drawer-link--active' : ''}`}
            onClick={() => setDrawerAnalysisOpen(open => !open)}
            aria-expanded={drawerAnalysisOpen}
          >
            <i className="pi pi-chart-bar"></i>
            <span>Research & Analysis</span>
            <i className={`pi pi-chevron-down nb-drawer-chevron${drawerAnalysisOpen ? ' nb-drawer-chevron--open' : ''}`}></i>
          </button>
          {drawerAnalysisOpen && (
            <div className="nb-drawer-submenu">
              {ANALYSIS_ITEMS.map(item => (
                <button
                  key={item.path}
                  className={`nb-drawer-link nb-drawer-subitem${item.match(location.pathname) ? ' nb-drawer-link--active' : ''}`}
                  onClick={() => go(item.path)}
                >
                  <NavItemIcon icon={item.icon} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
          {hasPermission('membership.manage') && (
            <button className={`nb-drawer-link${location.pathname.startsWith('/team') ? ' nb-drawer-link--active' : ''}`} onClick={() => go('/team')}>
              <i className="pi pi-users"></i><span>Team</span>
            </button>
          )}
          {NAV_ITEMS.slice(2).map(item => (
            <button
              key={item.path}
              className={`nb-drawer-link${item.match(location.pathname) ? ' nb-drawer-link--active' : ''}`}
              onClick={() => go(item.path)}
            >
              <NavItemIcon icon={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
          <button className="nb-drawer-link nb-drawer-logout" onClick={handleLogout}>
            <i className="pi pi-sign-out"></i>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
